import { APIError, Environment } from './models/common';
import * as qs from 'qs';

const servers: { [key in Environment]: string } = {
  dev: 'http://localhost:6363',
  staging: 'https://app.notablehealth.com',
  demo: 'https://demo.vivaa.engineering',
  prod: 'https://api.vivaa.engineering',
  webbucket: 'https://app.notablehealth.com',

  // web apps are served from API server either directly (prod) or via proxy (dev)
  weblocal: '/api',
  webprod: '',
};

export class Common {
  env: Environment;
  server: string;
  sid: string | null;
  constructor(env: Environment) {
    this.env = env;
    this.server = env ? servers[env] : servers.prod;
    this.sid = null;
  }

  setEnv(env: Environment) {
    this.env = env;
    this.server = servers[env] || servers.prod;
  }

  setSid(sid: string) {
    this.sid = sid;
  }

  constructPath(path: string, query?: object): string {
    if (path.slice(0, 1) !== '/') {
      path = '/' + path;
    }
    if (query) {
      return `${this.server}${path}?${qs.stringify(query)}`;
    }
    return `${this.server}${path}`;
  }

  constructWsPath(path: string) {
    // local dev case because http calls are proxy'ed but ws calls are not
    const isLocalDev = window.location.hostname === 'localhost';
    return `${(isLocalDev
      ? window.location.origin.replace('3004', '6363')
      : window.location.origin
    ).replace(/^(http|https)?/, 'ws')}${path}`;
  }

  authedFetch(url: string, init?: RequestInit): Promise<Response> {
    // Cannot be undefined for old Edge versions
    const headers = new Headers(init?.headers ?? {});
    if (this.sid) {
      headers.append('cookie', `connect.sid=${this.sid}`);
    }
    return fetch(url, {
      ...init,
      credentials: init?.credentials ?? 'same-origin',
      headers,
    });
  }

  async getJSON(
    url: string,
    init?: RequestInit,
    returnErrorStatus: boolean = false,
  ) {
    const response = await this.authedFetch(url, init);

    if (!response.ok && returnErrorStatus) {
      return Promise.reject({
        status: response.status,
        message: await response.text(),
      });
    }
    if (response.status === 204) return {};
    const data = await response.json();
    if (response.status >= 400) {
      console.log(
        `api.common.getJSON : got error status of ${response.status}, throwing error`,
      );
      throw new Error(JSON.stringify(data));
    }
    return data;
  }

  async getBlob(
    url: string,
    init?: RequestInit,
  ): Promise<{ status: number; dataURL: string }> {
    const response = await this.authedFetch(url, init);
    const { status } = response;
    const blob = await response.blob();
    return { status, dataURL: URL.createObjectURL(blob) };
  }

  async postJSON(
    url: string,
    body: Record<string, any> | undefined,
    method = 'POST',
    init?: RequestInit,
    returnErrorStatus: boolean = false,
  ) {
    const response = await this.authedFetch(url, {
      ...init,
      method,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok && returnErrorStatus) {
      return Promise.reject({
        status: response.status,
        message: await response.text(),
      });
    }
    const textResponse = await response.text();
    if (!textResponse) return null;

    let jsonResponse;
    try {
      jsonResponse = JSON.parse(textResponse);
    } catch (err) {
      throw new Error(`Response was not json: ${textResponse}`);
    }

    if (response.status >= 400) {
      console.log(
        `api.common.postJSON : got error status of ${response.status}, throwing error`,
        jsonResponse,
      );
      throw new APIError(jsonResponse, response.status);
    }
    return jsonResponse;
  }

  async putJSON(url: string, body?: Record<string, any>) {
    return await this.postJSON(url, body, 'PUT');
  }

  async patchJSON(url: string, body?: Record<string, any>) {
    return await this.postJSON(url, body, 'PATCH');
  }

  async deleteJSON(url: string, init?: RequestInit) {
    const response = await this.authedFetch(url, {
      ...init,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 204) {
      console.log('success with 204 status code');
      return;
    }

    const jsonResponse = await response.json();
    if (response.status >= 400) {
      console.log(
        `api.common.postJSON : got error status of ${response.status}, throwing error`,
        jsonResponse,
      );
      throw new Error(JSON.stringify(jsonResponse));
    }
    return jsonResponse;
  }
}

export class BaseAPI {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }
}
